// Turns raw Supabase/GoTrue auth errors into user-facing messages.
// GoTrue error `code` is the stable signal; message-sniffing is a fallback
// for older SDK versions that don't populate `code`.
type AuthErrorLike = {
  message?: string;
  status?: number;
  code?: string;
} | null | undefined;

const PASSWORD_REQUIREMENTS_MESSAGE =
  "Password must be at least 8 characters and include uppercase, lowercase, a number, and a special character.";

export function getAuthErrorMessage(error: AuthErrorLike): string {
  if (!error) return "";
  const message = error.message || "";
  const code = (error as any).code;

  const isRateLimited =
    error.status === 429 ||
    code === "over_email_send_rate_limit" ||
    code === "over_request_rate_limit" ||
    /rate limit/i.test(message);
  if (isRateLimited) {
    const secondsMatch = message.match(/(\d+)\s*second/i);
    if (secondsMatch) {
      const secs = secondsMatch[1];
      return `Too many requests. Please wait ${secs} second${secs === "1" ? "" : "s"} and try again.`;
    }
    const minutesMatch = message.match(/(\d+)\s*minute/i);
    if (minutesMatch) {
      const mins = minutesMatch[1];
      return `Too many requests. Please wait ${mins} minute${mins === "1" ? "" : "s"} and try again.`;
    }
    // GoTrue's over_email_send_rate_limit error doesn't include a wait
    // time and the real cooldown window can be well over a minute — don't
    // claim a specific duration we don't actually know.
    return "Too many requests. Please wait a while and try again.";
  }

  if (code === "invalid_credentials" || /invalid login credentials/i.test(message)) {
    return "Incorrect email or password. Please check your details and try again.";
  }

  if (code === "weak_password" || /password.*should contain|password.*at least one character/i.test(message)) {
    return PASSWORD_REQUIREMENTS_MESSAGE;
  }

  return message || "Something went wrong. Please try again.";
}
